<script setup lang="ts">
import { computed, watch } from "vue";
import { useFormLogic } from "../services/useFormLogic";
import { allForms } from "../config/messageForm";
import { useAppStore } from "@/store/app";

const appStore = useAppStore();
const service = appStore.getService;
const isFormActive = computed(() => appStore.getFormVisibility);
const formSchema = computed(() => {
  return allForms[service] || allForms['gazeta'];
});
const formButtonDisabled = computed(() => appStore.getButtonDisabled);

// Resetuj formularz każdorazowo gdy modal się otwiera
watch(isFormActive, (newValue) => {
  if (newValue) {
    resetForm();
    const formData = appStore.getFormData;
    if (formData) {
      formInputs.userName = formData.userName || '';
      formInputs.userEmail = formData.userEmail || '';
    }
  }
});

const close = (type: string) => {
  appStore.closeElements(type);
};
const {
  selectList,
  selectDataSet,
  isHelperRequired,
  helperTextArea,
  isExplainIncorrect,
  showExplainWarning,
  changeSelect,
  checkField,
  submitForm,
  formInputs,
  resetForm
} = useFormLogic();

</script>

<template>
  <form v-if="formSchema && isFormActive" @submit.prevent="submitForm" class="dsa__report" id="dsa__report">
    <div @click="close('all')" class="dsa__reportCloseBTN"></div>
    <div class="dsa__reportHeader">{{ formSchema.header }}</div>
    <div class="dsa__reportText">{{ formSchema.text }}</div>

    <!-- custom select -->
    <div class="dsa__reportSelectBox">
      <div class="dsa__reportLabel">
        {{ formSchema.reasonLabel }}
        <span class="dsa__reportWarning">*</span>
      </div>

      <div class="dsa__reportSelectWrapper" :class="{ 'dsa__reportSelectWrapper--showList': selectList }"
        @click="selectList = !selectList">
        <div class="dsa__reportInput dsa__reportSelectInput dsa__reportJS" type="reason" name="reason" id="reason"
          :data-value="selectDataSet" ref="select">{{formInputs.reason}}</div>

        <div class="dsa__reportSelectArrow"></div>

        <div v-if="selectList" class="dsa__reportSelectList">
          <div v-for="option in formSchema.customSelectOptions" :key="option.value" class="dsa__reportSelectItem"
            @click="changeSelect" :data-value="option.value">
            {{ option.name }}
          </div>
        </div>

      </div>

      <div class="dsa__reportHelper">{{ formSchema.reasonHelper }}</div>
    </div>

    <!-- textarea -->
    <label class="dsa__reportLabel">
      {{ formSchema.explainLabel }}
      <span v-if="showExplainWarning" class="dsa__reportWarning">*</span>
    </label>
    <textarea 
      v-model.trim="formInputs.explain" 
      type="textarea" 
      name="explain"
      @input="(e) => checkField(e.target)" 
      :class="{ 'dsa__reportInput--error': isExplainIncorrect }"
      class="dsa__reportInput dsa__reportInput--textarea dsa__reportJS">
    </textarea>
    <div v-if="isHelperRequired" class="dsa__reportHelper">{{ helperTextArea }}</div>

    <!-- userName -->
    <label class="dsa__reportLabel" for="userName">
      {{ formSchema.userNameLabel }}
      <span class="dsa__reportLabel--regular">{{ formSchema.optional }}</span>
    </label>
    <input v-model.trim="formInputs.userName" @input="(e) => checkField(e.target)" class="dsa__reportInput dsa__reportJS"
      type="text" name="userName" />


    <!-- userEmail -->
    <label class="dsa__reportLabel" for="userMail">
      {{ formSchema.userMailLabel }}
      <span class="dsa__reportLabel--regular">{{ formSchema.optional }}</span>
    </label>
    <input 
      v-model.trim="formInputs.userEmail" 
      @input="(e) => checkField(e.target)" 
      class="dsa__reportInput dsa__reportJS"
      type="email" 
      name="userMail" 
      id="userMail"
    />
    <div class="dsa__reportHelper">
      {{ formSchema.userMailHelper }}
    </div>

    <!-- statute -->
    <div class="dsa__reportStatuteBox">
      <input v-model="formInputs.rules" @input="(e) => checkField(e.target)" class="dsa__reportStatuteInput dsa__reportJS"
        type="checkbox" name="statute" id="statute" />
      <label class="dsa__reportStatuteLabel" for="statute">
        <span class="dsa__reportWarning">*</span>
        {{ formSchema.statuteLabel }}
      </label>
    </div>

    <!-- footer -->
    <div class="dsa__reportFooter">
      {{ formSchema.footer }}
      <a :href="formSchema.footerSpanLink" target="_blank" class="dsa__reportBlack">{{ formSchema.footerSpan }}</a>
    </div>

    <!-- buttons -->
    <div class="dsa__reportButtons">
      <button @click="close('all')" type="button" class="dsa__reportButton dsa__reportButton--ghost">
        {{ formSchema.btn_cancel }}
      </button>
      <button type="submit" class="dsa__reportButton dsa__reportButton--main" data-entryid="68069093"
        :disabled="formButtonDisabled">

        <span v-if="!formButtonDisabled" class="text">{{ formSchema.btn_report }}</span>
        <span class="dsa__reportLoader" :class="{ 'dsa__reportLoader--active': formButtonDisabled }"></span>
      </button>
      <div v-if="formSchema.mobileCloseButton" @click="() => close('all')"
        class="dsa__reportButton dsa__reportButton--underline">
        Zamknij
      </div>
    </div>
  </form>
</template>

<style lang="scss">
.dsa__report {
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20000;
  padding: 30px;
  background-color: #fff;
  color: #111;
  width: 390px;
  margin: 0 auto;
  max-height: 100vh;
  overflow-y: auto;

  &CloseBTN {
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 0;
    width: 50px;
    height: 50px;

    &::before {
      content: "";
      background-repeat: no-repeat;
      background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMiIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDEyIDEyIj4KICA8cGF0aCBpZD0iemFta25paiIgZD0iTTE3LjMwOCw3LDEzLDExLjMwOCw4LjY5Miw3LDcsOC42OTIsMTEuMzA4LDEzLDcsMTcuMzA4LDguNjkyLDE5LDEzLDE0LjY5MiwxNy4zMDgsMTksMTksMTcuMzA4LDE0LjY5MiwxMywxOSw4LjY5MloiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC03IC03KSIgZmlsbD0iIzQ0NCIvPgo8L3N2Zz4K');
      background-position: center;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }
  }

  &Header {
    font: 700 22px/28px Oswald, sans-serif;
    text-transform: uppercase;
    letter-spacing: normal;
    top: 0;
    left: 0;
    transform: none;
    width: 100%;
  }

  &Text {
    font: 400 14px/18px Roboto, sans-serif;
    margin: 10px 0;
    color: #111111;
  }

  &Label {
    display: block;
    color: #111111;
    font: 700 14px/18px Roboto Condensed, sans-serif;
    margin-top: 20px;

    &--regular {
      font-weight: 400;
    }
  }

  &Input {
    margin-top: 10px;
    appearance: none;
    background-color: #fff;
    color: #111;
    font: 400 14px/18px Roboto, sans-serif;
    padding: 12px 15px;
    width: auto;
    border: 1px solid #ccc;
    position: relative;
    transition: border-color .2s ease-out;
    display: flex;
    align-items: center;
    gap: 10px;
    align-self: stretch;
    height: 40px;

    &:hover {
      border-color: #111;
    }

    &:focus {
      border-color: #111;
      outline: 1px solid #111;

      &.dsa__reportError {
        border-color: #e30000;
      }
    }

    &::placeholder {
      color: #757575;
    }

    &--error {
      border-color: #e30000;
      outline-color: #e30000;
    }

    &--textarea {
      min-height: 68px;
      min-width: 100%;
      max-width: 100%;
      color: #111111 !important;
    }
  }

  &Select {
    &Box {
      position: relative;
    }

    &Wrapper {
      position: relative;

      &--showList {
        .dsa__reportSelectArrow {
          transform: translateY(-50%) rotate(180deg);
        }
      }
    }

    &Arrow {
      position: absolute;
      background-image: url('data:image/svg+xml;base64,PHN2ZyBpZD0iR3JvdXBfNjI4MyIgZGF0YS1uYW1lPSJHcm91cCA2MjgzIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij4KICA8cmVjdCBpZD0iUmVjdGFuZ2xlXzQ0NCIgZGF0YS1uYW1lPSJSZWN0YW5nbGUgNDQ0IiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9Im5vbmUiLz4KICA8cGF0aCBpZD0iUGF0aF83OTkiIGRhdGEtbmFtZT0iUGF0aCA3OTkiIGQ9Ik0xMiwxNSw3LDEwSDE3WiIgZmlsbD0iIzExMSIvPgo8L3N2Zz4K');
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
    }

    &Input {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      // overflow: hidden;
      -webkit-box-orient: vertical;
      color: #757575;
      line-height: 24px;
      padding: 8px 15px;

      &--active {
        color: #111;
      }
    }

    &List {
      background-color: #fff;
      box-shadow: 0 0 16px #00000033;
      display: flex;
      flex-direction: column;
      left: 0;
      position: absolute;
      width: 100%;
      top: 100%;
      z-index: 100;
      padding: 10px 0;
      transition: all 0.3s ease-out;
      overflow-y: scroll;
      max-height: 400px;
    }

    &Item {
      background-color: #ffffff;
      border-radius: 0;
      color: #111;
      font: 400 14px/18px Roboto, sans-serif;
      padding: 10px 15px;
      position: relative;
      transition: border-color 0.2s ease-out;

      &:hover {
        cursor: pointer;
        background-color: #F5F5F5;
      }
    }
  }

  &Helper {
    font: 400 12px/16px Roboto, sans-serif;
    color: #757575;
    margin-top: 10px;
  }

  &Statute {
    &Box {
      line-height: 16px;
      margin: 30px 0 0 0;
      display: flex;
      align-items: self-start;
    }

    &Input {
      width: 14px;

      accent-color: #111;
      margin-right: 10px;
    }

    &Label {
      font: 400 12px/16px Roboto, sans-serif;
      color: #111;

      &.dsa__reportError {
        color: #DC1A21;
      }
    }
  }

  &Footer {
    margin-top: 30px;
    color: #757575;
    font: 400 10px/13px Roboto, sans-serif;
    
  }

  &Buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 30px;
    gap: 0 20px;
  }

  &Button {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 11px 20px;
    flex: 1;
    gap: 10px;
    font: 700 14px/14px Roboto Condensed, sans-serif;
    text-transform: uppercase;
    border-radius: 3px;
    cursor: pointer;
    transition: all .2s ease-out;
    margin-right: 0;
    white-space: nowrap;
    height: 40px;

    &--main {
      background-color: #beff96;
      border: 2px solid #beff96;
      &:hover{
        background-color: #A9F27C;
        border-color: #A9F27C;
      }
    }

    &--ghost {
        background-color: #ffffff;
        border: 2px solid #E5E5E5;

        &:hover{
          background-color: #F5F5F5;
          border-color: #D3D3D3;
        }
    }

    &--underline {
      display: none;
    }

    &:focus, &:active{
      border: 2px solid #111111;
    }
  }

  &Warning {
    color: #E30000;

    //lukixp  
    &--required {
      display: none;
    }

    &--optional {
      display: none;
    }
  }

  &Black {
    color: #111111;
  }

  &Error {
    border-color: #E30000;
    color: #E30000;
    outline: #E30000 !important;
    // color: #DC1A21;
  }

  &Loader {
    display: none;
    width: 16px;
    height: 16px;
    border: 2px solid white;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;

    &--active {
      display: block;
    }
  }
}

 .dsa__widget--Sport .dsa__report {
    &Button{
      &--main {
        background-color: #f03c3c;
        border: 2px solid #f03c3c;
        color: #ffffff;
        &:hover{
          background-color: #DB2222;
          border-color: #DB2222;
        }
      }
    }
  }

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media all and (max-width: 932px) {
  .dsa__report {
    width: 100vw;
    height: 100dvh;
    padding: 30px 15px;
    top: 0;
    left: 0;
    transform: translate(0, 0);
    margin: 0;

    &Buttons {
      gap: 20px;
    }

    &Button {
      width: 100%;

      &--underline {
        display: block;
      }
    }

    &Header{
      top: 0;
      left: 0;
      transform: none;
      width: 100%;
    }
  }
}
</style>
